import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/DashboardShell";
import { StatusPill } from "@/components/brand";
import { PLAN_BY_ID } from "@/constants/routa";
import api from "@/lib/api";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "", label: "All" },
];

export default function AdminMerchants() {
  const [merchants, setMerchants] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/merchants", { params: filter ? { status: filter } : {} });
    setMerchants(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setApproval = async (m, status) => {
    let reject_reason = null;
    if (status === "rejected") {
      reject_reason = window.prompt("Reason for rejecting this merchant (optional):") || "";
    }
    try {
      await api.patch(`/merchants/${m.id}/approval`, { status, reject_reason });
      toast.success(`Merchant ${status}`);
      load();
    } catch { toast.error("Action failed"); }
  };

  const toggleSuspend = async (m) => {
    const next = m.status === "suspended" ? "active" : "suspended";
    try { await api.patch(`/users/${m.id}/status`, null, { params: { status: next } }); toast.success(`Merchant ${next}`); load(); }
    catch { toast.error("Action failed"); }
  };

  const planName = (id) => PLAN_BY_ID[id]?.name || "—";

  return (
    <div>
      <PageHeader index="03" eyebrow="Merchant Roster" title="Merchants" tag="Approvals & accounts" />

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`merchant-filter-${f.key || "all"}`}
            className={`px-3.5 py-1.5 font-mono text-[11px] tracking-[0.1em] uppercase border transition-colors ${filter === f.key ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "border-gray-300 text-gray-500 hover:border-[#0a0a0a]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-gray-200 divide-y divide-gray-200" data-testid="merchant-roster">
        {merchants.length === 0 && <div className="px-5 py-10 text-center text-sm text-gray-400">No merchants in this view.</div>}
        {merchants.map((m) => (
          <div key={m.id} data-testid={`merchant-row-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between px-5 py-4 gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-semibold text-[#0a0a0a]">{m.company_name || m.name}</span>
                  <StatusPill status={m.merchant_status || "approved"} />
                  <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-orange-600">{planName(m.plan)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 font-mono">{m.email} · {m.order_count} orders · joined {new Date(m.created_at).toLocaleDateString()}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(m.merchant_status || "approved") === "pending" && (
                  <>
                    <button onClick={() => setApproval(m, "approved")} data-testid={`approve-merchant-${m.id}`} className="font-mono text-[11px] tracking-[0.1em] uppercase bg-[#0a0a0a] text-white px-3 py-1.5 hover:bg-[#262626]">Approve</button>
                    <button onClick={() => setApproval(m, "rejected")} data-testid={`reject-merchant-${m.id}`} className="font-mono text-[11px] tracking-[0.1em] uppercase border border-orange-500 text-orange-600 px-3 py-1.5 hover:bg-orange-50">Reject</button>
                  </>
                )}
                {(m.merchant_status || "approved") === "approved" && (
                  <button onClick={() => toggleSuspend(m)} data-testid={`toggle-merchant-${m.id}`}
                    className={`font-mono text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 border transition-colors ${m.status === "suspended" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "border-orange-500 text-orange-600 hover:bg-orange-50"}`}>
                    {m.status === "suspended" ? "Reactivate" : "Suspend"}
                  </button>
                )}
                {(m.merchant_status || "approved") === "rejected" && (
                  <button onClick={() => setApproval(m, "approved")} data-testid={`approve-merchant-${m.id}`} className="font-mono text-[11px] tracking-[0.1em] uppercase bg-[#0a0a0a] text-white px-3 py-1.5 hover:bg-[#262626]">Approve</button>
                )}
                <button onClick={() => setOpenId(openId === m.id ? null : m.id)} data-testid={`view-merchant-${m.id}`} className="font-mono text-[11px] tracking-[0.1em] uppercase text-gray-500 hover:text-[#0a0a0a] px-2">
                  {openId === m.id ? "Hide" : "View"}
                </button>
              </div>
            </div>
            {openId === m.id && (
              <div className="px-5 pb-5 grid sm:grid-cols-2 gap-x-8 gap-y-3 bg-gray-50" data-testid={`merchant-details-${m.id}`}>
                {[
                  ["Contact name", m.name],
                  ["Business name", m.company_name || "—"],
                  ["Email", m.email],
                  ["Phone", m.phone || "—"],
                  ["Business address", m.business_address || "—"],
                  ["Region", m.region || "—"],
                  ["Selected plan", planName(m.plan)],
                  ["Signup date", new Date(m.created_at).toLocaleString()],
                  ["Approval status", (m.merchant_status || "approved")],
                ].map(([k, v]) => (
                  <div key={k} className="pt-3">
                    <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-gray-400">{k}</div>
                    <div className="text-sm text-[#0a0a0a] capitalize">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
